import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, FileText } from "lucide-react";

export default function Welcome() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A1A2F] flex flex-col" data-testid="welcome-page">
      {/* Top nav */}
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
          <Link to="/" data-testid="eyb-logo" className="inline-flex items-center gap-4">
            <img
              src="/eyb-logo-horizontal.png"
              alt="EYB Consulting"
              className="h-12 md:h-14 w-auto"
            />
            <span className="hidden sm:block text-[11px] md:text-[12px] text-[#6B7280] leading-tight border-l border-[#E5E7EB] pl-4 max-w-[220px]">
              Clarity and Control, Built for Your Business
            </span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex-1 overflow-hidden hero-grid">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1618858553936-84170aa4be58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyZSUyMG5hdnklMjB3aGl0ZXxlbnwwfHx8fDE3Nzg1NTcyNDB8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover opacity-[0.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1A2F] via-[#0A1A2F]/95 to-[#0F2540]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <h1 className="font-display font-extrabold text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              Scope of Work{" "}
              <span className="text-[#1FA8E0]">
                Generator
              </span>
            </h1>

            <p className="mt-8 text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl">
              Answer a short, structured set of questions about your business. We'll surface
              the priorities that matter and prepare a professional scope of work — ready for
              your advisor to review and refine.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <button
                data-testid="start-assessment-btn"
                onClick={() => navigate("/client")}
                className="group inline-flex items-center gap-3 bg-[#1FA8E0] hover:bg-[#4FB8E8] text-white px-8 py-4 rounded-md cta-glow transition-all duration-300"
              >
                <span className="text-sm font-semibold tracking-wide">Start Assessment</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </button>
              <a
                href="#how-it-works"
                data-testid="learn-more-btn"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/90 border border-white/10 px-7 py-4 rounded-md transition-all duration-300 text-sm font-medium"
              >
                Learn More
              </a>
            </div>

            {/* Three feature columns */}
            <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl">
              {[
                { icon: Sparkles, label: "Diagnostic", text: "Categories of business health." },
                { icon: ShieldCheck, label: "Confidential", text: "Answers stay in your browser." },
                { icon: FileText, label: "Export-ready", text: "Polished Word document, branded." },
              ].map((f, i) => (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                  className="border-t border-white/10 pt-5"
                >
                  <f.icon className="h-5 w-5 text-[#1FA8E0] mb-3" strokeWidth={1.6} />
                  <div className="text-[11px] tracking-[0.22em] uppercase text-white font-semibold">{f.label}</div>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{f.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#1FA8E0] mb-3">Process</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl text-[#0A1A2F] tracking-tight">
              How It Works
            </h2>
            <p className="mt-4 text-[#6B7280] text-base md:text-lg">
              Four simple steps to a professional, advisor-ready scope of work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {[
              { n: "01", title: "Client details", text: "Tell us about the business — name, industry, size and location." },
              { n: "02", title: "Diagnostic", text: "Move through 12 short categories covering cash, profit and growth." },
              { n: "03", title: "Insight", text: "Review a risk summary, priority areas and recommended services." },
              { n: "04", title: "Scope of Work", text: "Download a branded Word document ready for advisor review." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="relative"
              >
                <div className="font-display font-extrabold text-6xl text-[#E5E7EB] leading-none mb-4">{s.n}</div>
                <div className="font-display font-bold text-lg text-[#0A1A2F] mb-2">{s.title}</div>
                <p className="text-sm text-[#6B7280] leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1A2F] border-t border-white/10 py-8 px-6 md:px-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <img
            src="/eyb-logo-horizontal-white.png"
            alt="EYB Consulting"
            className="h-8 w-auto opacity-90"
          />
          <span className="text-xs text-white/40 tracking-wide">
            © {new Date().getFullYear()} EYB Consulting · Strategic advisory for growing businesses
          </span>
        </div>
      </footer>
    </div>
  );
}
